import { Wallet, FileText, FileImage } from "lucide-react";
import Flag from "react-world-flags";
import { PublicAccountPaymentMethod } from "../types/public-accounts-api.types";
import type { NewPublicAccountFormData } from "../types/new-public-account.types";
import { InputSelect } from "@/shared/components/form/InputSelect";
import { FieldError } from "@/shared/components/form/FieldError";
import { MultiFileUpload } from "../../payments/components/MultiFileUpload";
import {
  PUBLIC_ACCOUNT_COUNTRY_OPTIONS,
  COUNTRY_CODE_TO_CURRENCY,
  COUNTRY_CODE_TO_ISO2,
  CRYPTO_NETWORK_OPTIONS,
  isCryptoWalletPublicCountryCode,
  shouldShowAccountTypeAndNumberFields,
} from "../constants/public-accounts.constants";
import {
  getBankOptionsForCountry,
  getPaymentMethodFieldsForAccount,
} from "../constants/bankOptionsByCountry";
import { STRUCTURED_FIELD_TO_TKEY } from "../constants/structured-data-labels.constants";
import {
  METHOD_FIELD_PLACEHOLDER_KEYS,
  METHOD_OPTION_LABEL_KEYS,
} from "../constants/form-method-field-translations";
import { isPdfUrl } from "../utils/attachmentUrlHelpers";
import type { NewPublicAccountFormErrors } from "../utils/validateNewPublicAccountForm";
import type { PaymentMethodFieldConfig } from "../../payments/types/new-payment.types";
import useTranslations from "@/shared/lang/useTranslations";

const inputBase =
  "w-full bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 dark:text-white transition-all h-12 px-4";
const labelBase = "block text-sm font-medium text-gray-600 dark:text-gray-400";

function formatSelectLabel(label: string): string {
  if (!label) return label;
  return label.charAt(0).toUpperCase() + label.slice(1).toLowerCase();
}

function getPaymentMethodOptions(
  t: (k: keyof import("@/shared/lang/interfaces/accountsWallets").AccountsWalletsTranslations) => string
) {
  return [
    { value: PublicAccountPaymentMethod.BANK, label: t("form_payment_bank") },
    { value: PublicAccountPaymentMethod.CRYPTO_WALLET, label: t("form_payment_crypto") },
  ];
}
function getAccountTypeOptions(
  t: (k: keyof import("@/shared/lang/interfaces/accountsWallets").AccountsWalletsTranslations) => string
) {
  return [
    { value: "ahorros", label: t("form_account_type_savings") },
    { value: "corriente", label: t("form_account_type_checking") },
  ];
}

function ExtraMethodField({
  field,
  label,
  placeholder,
  options,
  value,
  onChange,
  error,
  disabled,
  selectPlaceholder,
}: {
  field: PaymentMethodFieldConfig;
  label: string;
  placeholder?: string;
  options?: { value: string; label: string }[];
  value: string;
  onChange: (v: string) => void;
  error?: string;
  disabled: boolean;
  selectPlaceholder: string;
}) {
  const inputId = `account-method-${field.id}`;
  if (field.type === "select") {
    return (
      <div className="space-y-2">
        <label htmlFor={inputId} className={labelBase}>
          {label}
        </label>
        <select
          id={inputId}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          className={inputBase}
        >
          <option value="">
            {placeholder ?? selectPlaceholder}
          </option>
          {(options ?? []).map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <FieldError error={error} />
      </div>
    );
  }
  return (
    <div className="space-y-2">
      <label htmlFor={inputId} className={labelBase}>
        {label}
      </label>
      <input
        id={inputId}
        type={field.type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        className={inputBase}
      />
      <FieldError error={error} />
    </div>
  );
}

type AddAccountFormProps = {
  value: NewPublicAccountFormData;
  onChange: (data: NewPublicAccountFormData) => void;
  errors?: NewPublicAccountFormErrors;
  disabled?: boolean;
  isEditMode?: boolean;
};

export function AddAccountForm({
  value,
  onChange,
  errors = {},
  disabled = false,
  isEditMode = false,
}: AddAccountFormProps) {
  const { t } = useTranslations("accountsWallets");
  const paymentMethodOptions = getPaymentMethodOptions(t);
  const accountTypeOptions = getAccountTypeOptions(t);
  const update = <K extends keyof NewPublicAccountFormData>(
    field: K
  ) => (val: NewPublicAccountFormData[K] | { value: string; label: string }) => {
    if (typeof val === "object" && val !== null && "value" in val && "label" in val) {
      onChange({ ...value, [field]: (val as { value: string }).value });
      return;
    }
    onChange({ ...value, [field]: val as NewPublicAccountFormData[K] });
  };

  const countryOption = PUBLIC_ACCOUNT_COUNTRY_OPTIONS.find((o) => o.value === value.country_code);
  const paymentOption = paymentMethodOptions.find((o) => o.value === value.payment_method);

  const bankOptions = getBankOptionsForCountry(value.country_code);
  const hasBankSelect = bankOptions.length > 0;
  const methodFieldsConfig = getPaymentMethodFieldsForAccount(
    value.country_code,
    value.bank_name
  );
  const extraMethodFields = methodFieldsConfig.filter(
    (f) => f.id !== "tipoCuenta" && f.id !== "numeroCuenta"
  );
  const showExtraFields = extraMethodFields.length > 0;
  const showAccountTypeAndNumber = shouldShowAccountTypeAndNumberFields(
    value.country_code,
    value.bank_name
  );

  const handleCountryChange = (
    newValue: { value: string; label: string } | { value: string; label: string }[] | null
  ) => {
    const opt = Array.isArray(newValue) ? newValue[0] ?? null : newValue;
    if (!opt) {
      onChange({
        ...value,
        country_code: "",
        currency: "",
        bank_name: "",
        methodFields: {},
      });
      return;
    }
    const currency = COUNTRY_CODE_TO_CURRENCY[opt.value] ?? "";
    const isCryptoCountry = isCryptoWalletPublicCountryCode(opt.value);
    const newBankOptions = getBankOptionsForCountry(opt.value);
    const keepBank =
      !isCryptoCountry &&
      (newBankOptions.length === 0 || newBankOptions.some((o) => o.value === value.bank_name));
    onChange({
      ...value,
      country_code: opt.value,
      currency,
      payment_method: isCryptoCountry
        ? PublicAccountPaymentMethod.CRYPTO_WALLET
        : PublicAccountPaymentMethod.BANK,
      ...(keepBank ? {} : { bank_name: "", methodFields: {} }),
    });
  };

  const handleBankChange = (
    newValue: { value: string; label: string } | { value: string; label: string }[] | null
  ) => {
    const opt = Array.isArray(newValue) ? newValue[0] ?? null : newValue;
    const bank = opt?.value ?? "";
    onChange({
      ...value,
      bank_name: bank,
      methodFields: {},
    });
  };

  return (
    <form className="space-y-6" onSubmit={(e) => e.preventDefault()}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <label className={labelBase}>{t("form_label_country")}</label>
          <InputSelect<{ value: string; label: string }>
            options={PUBLIC_ACCOUNT_COUNTRY_OPTIONS}
            value={countryOption ?? null}
            onChange={handleCountryChange}
            placeholder={t("form_select_country")}
            isDisabled={disabled}
            formatOption={(opt) => {
              const iso2 = COUNTRY_CODE_TO_ISO2[opt.value];
              return (
                <span className="flex items-center gap-2">
                  {iso2 ? (
                    <Flag code={iso2} className="h-4 w-6 rounded object-cover shrink-0" />
                  ) : (
                    <span className="flex h-4 w-6 items-center justify-center shrink-0 text-slate-500 dark:text-slate-400">
                      <Wallet className="h-4 w-4" />
                    </span>
                  )}
                  <span>{formatSelectLabel(opt.label)}</span>
                </span>
              );
            }}
            formatSelectedValue={(opt) => {
              const iso2 = COUNTRY_CODE_TO_ISO2[opt.value];
              return (
                <span className="flex items-center gap-2">
                  {iso2 ? (
                    <Flag code={iso2} className="h-4 w-6 rounded object-cover shrink-0" />
                  ) : (
                    <span className="flex h-4 w-6 items-center justify-center shrink-0 text-slate-500 dark:text-slate-400">
                      <Wallet className="h-4 w-4" />
                    </span>
                  )}
                  <span>{formatSelectLabel(opt.label)}</span>
                </span>
              );
            }}
          />
          <FieldError error={errors.country_code} />
          {value.country_code && (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {t("form_currency_label")}:{" "}
              <span className="font-medium">
                {COUNTRY_CODE_TO_CURRENCY[value.country_code] ?? value.currency}
              </span>
            </p>
          )}
          <FieldError error={errors.currency} />
        </div>

        <div className="space-y-2">
          <label className={labelBase}>{t("form_label_payment_method")}</label>
          <InputSelect<{ value: string; label: string }>
            options={paymentMethodOptions}
            value={paymentOption ?? null}
            onChange={(newValue) => {
              const opt = Array.isArray(newValue) ? newValue[0] ?? null : newValue;
              if (opt) update("payment_method")(opt);
            }}
            placeholder={t("form_select_generic")}
            isDisabled={disabled}
          />
          <FieldError error={errors.payment_method} />
        </div>

        <div className="space-y-2 md:col-span-2">
          <label htmlFor="internal_alias" className={labelBase}>
            {t("form_label_internal_alias")}
          </label>
          <input
            id="internal_alias"
            type="text"
            value={value.internal_alias}
            onChange={(e) => update("internal_alias")(e.target.value)}
            placeholder={t("form_internal_alias_placeholder")}
            disabled={disabled}
            className={inputBase}
          />
        </div>

        {value.payment_method === PublicAccountPaymentMethod.BANK && (
          <>
            <div className="space-y-2">
              <label className={labelBase}>
                {isCryptoWalletPublicCountryCode(value.country_code)
                  ? t("form_label_red")
                  : t("form_label_bank_name")}
              </label>
              {hasBankSelect ? (
                <InputSelect<{ value: string; label: string }>
                  options={bankOptions}
                  value={bankOptions.find((o) => o.value === value.bank_name) ?? null}
                  onChange={handleBankChange}
                  placeholder={
                    isCryptoWalletPublicCountryCode(value.country_code)
                      ? t("form_select_red")
                      : t("form_select_bank")
                  }
                  isDisabled={disabled}
                />
              ) : (
                <input
                  id="bank_name"
                  type="text"
                  value={value.bank_name}
                  onChange={(e) => update("bank_name")(e.target.value)}
                  placeholder={t("form_bank_placeholder")}
                  disabled={disabled}
                  className={inputBase}
                />
              )}
              <FieldError error={errors.bank_name} />
            </div>
            {showExtraFields && (
              <div className="md:col-span-2 space-y-6">
                <h3 className="text-base font-semibold text-gray-800 dark:text-gray-200">
                  {t("form_extra_methods_title")}
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {extraMethodFields.map((field) => {
                    const labelKey = STRUCTURED_FIELD_TO_TKEY[field.id];
                    const placeholderKey = METHOD_FIELD_PLACEHOLDER_KEYS[field.id];
                    const translatedLabel = labelKey ? t(labelKey) : field.label;
                    const translatedPlaceholder = placeholderKey
                      ? t(placeholderKey)
                      : field.placeholder;
                    const translatedOptions = field.options?.map((opt) => ({
                      value: opt.value,
                      label: METHOD_OPTION_LABEL_KEYS[opt.value]
                        ? t(METHOD_OPTION_LABEL_KEYS[opt.value])
                        : opt.label,
                    }));
                    return (
                      <ExtraMethodField
                        key={field.id}
                        field={field}
                        label={translatedLabel}
                        placeholder={translatedPlaceholder}
                        options={translatedOptions}
                        value={(value.methodFields ?? {})[field.id] ?? ""}
                        onChange={(v) =>
                          onChange({
                            ...value,
                            methodFields: {
                              ...(value.methodFields ?? {}),
                              [field.id]: v,
                            },
                          })
                        }
                        error={errors.methodFields?.[field.id]}
                        disabled={disabled}
                        selectPlaceholder={t("form_select_field_placeholder", {
                          label: translatedLabel,
                        })}
                      />
                    );
                  })}
                </div>
              </div>
            )}
            {showAccountTypeAndNumber && (
              <>
                <div className="space-y-2">
                  <label className={labelBase}>{t("form_label_account_type")}</label>
                  <InputSelect<{ value: string; label: string }>
                    options={accountTypeOptions}
                    value={
                      accountTypeOptions.find((o) => o.value === value.account_type) ?? null
                    }
                    onChange={(newValue) => {
                      const opt = Array.isArray(newValue) ? newValue[0] ?? null : newValue;
                      update("account_type")(opt ? opt.value : "");
                    }}
                    placeholder={t("form_select_account_type")}
                    isDisabled={disabled}
                  />
                  <FieldError error={errors.account_type} />
                </div>
                <div className="space-y-2">
                  <label htmlFor="account_number" className={labelBase}>
                    {t("form_label_account_number")}
                  </label>
                  <input
                    id="account_number"
                    type="text"
                    value={value.account_number}
                    onChange={(e) => update("account_number")(e.target.value)}
                    placeholder={t("form_account_number_placeholder")}
                    disabled={disabled}
                    className={inputBase}
                  />
                  <FieldError error={errors.account_number} />
                </div>
              </>
            )}
          </>
        )}

        {value.payment_method === PublicAccountPaymentMethod.CRYPTO_WALLET && (
          <>
            <div className="space-y-2">
              <label className={labelBase}>{t("form_label_network")}</label>
              <InputSelect<{ value: string; label: string }>
                options={CRYPTO_NETWORK_OPTIONS}
                value={CRYPTO_NETWORK_OPTIONS.find((o) => o.value === value.network) ?? null}
                onChange={(newValue) => {
                  const opt = Array.isArray(newValue) ? newValue[0] ?? null : newValue;
                  update("network")(opt ? opt.value : "");
                }}
                placeholder={t("form_select_network")}
                isDisabled={disabled}
              />
              <FieldError error={errors.network} />
            </div>
            <div className="space-y-2">
              <label htmlFor="wallet_address" className={labelBase}>
                {t("form_label_wallet_address")}
              </label>
              <input
                id="wallet_address"
                type="text"
                value={value.wallet_address}
                onChange={(e) => update("wallet_address")(e.target.value)}
                placeholder={t("form_wallet_address_placeholder")}
                disabled={disabled}
                className={inputBase}
              />
              <FieldError error={errors.wallet_address} />
            </div>
          </>
        )}
      </div>

      <div className="space-y-2">
        <label htmlFor="notes" className={labelBase}>
          {t("form_label_notes")}
        </label>
        <textarea
          id="notes"
          value={value.notes}
          onChange={(e) => update("notes")(e.target.value)}
          placeholder={t("form_notes_placeholder")}
          disabled={disabled}
          rows={3}
          className={`${inputBase} h-auto py-3 resize-none min-h-[80px]`}
        />
      </div>

      {isEditMode ? (
        <div className="space-y-4 pt-2 border-t border-gray-200 dark:border-gray-700">
          <h3 className="text-base font-semibold text-gray-800 dark:text-gray-200">
            {t("form_documents_section_title")}
          </h3>
          {(value.existing_attachment_urls?.length ?? 0) > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {value.existing_attachment_urls!.map((url, i) => {
                const pdf = isPdfUrl(url);
                return (
                  <div
                    key={`${i}-${url.slice(0, 48)}`}
                    className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50/80 dark:bg-gray-900/50"
                  >
                    <div
                      className={`w-10 h-10 flex items-center justify-center rounded-lg shrink-0 ${
                        pdf
                          ? "bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400"
                          : "bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400"
                      }`}
                    >
                      {pdf ? (
                        <FileText className="h-5 w-5" aria-hidden />
                      ) : (
                        <FileImage className="h-5 w-5" aria-hidden />
                      )}
                    </div>
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300 truncate min-w-0">
                      {t("detail_file_n", { n: i + 1 })}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
          <MultiFileUpload
            value={value.attachments}
            onChange={(files) => update("attachments")(files)}
            label={t("form_attachments_upload_edit_label")}
            description={t("form_attachments_description")}
            disabled={disabled}
            inputId="edit-account-attachments"
            allowPdf
          />
        </div>
      ) : (
        <div className="space-y-2">
          <MultiFileUpload
            value={value.attachments}
            onChange={(files) => update("attachments")(files)}
            label={t("form_attachments_label")}
            description={t("form_attachments_description")}
            disabled={disabled}
            inputId="add-account-attachments"
            allowPdf
          />
        </div>
      )}
    </form>
  );
}
